import { useEffect, useRef } from 'react';

const VOICE_BANDS: Array<[number, number]> = [
  [80, 180],
  [180, 350],
  [350, 700],
  [700, 1400],
  [1400, 2200],
  [2200, 3600],
  [3600, 6200],
];

function readBands(analyser: AnalyserNode, data: Uint8Array, gain: number): number[] {
  analyser.getByteFrequencyData(data);
  const hz = analyser.context.sampleRate / analyser.fftSize;
  return VOICE_BANDS.map(([lo, hi]) => {
    const start = Math.max(0, Math.floor(lo / hz));
    const end = Math.min(data.length - 1, Math.ceil(hi / hz));
    let sum = 0;
    let count = 0;
    for (let i = start; i <= end; i++) {
      sum += data[i];
      count += 1;
    }
    const level = count ? sum / count : 0;
    return Math.min(1, Math.max(0, (level - 10) / gain));
  });
}

function bandEnergy(bands: number[]): number {
  return bands.reduce((sum, n) => sum + n, 0) / bands.length;
}

/** Seven bars driven by the live voice: the mic while you talk, the bot's audio while it talks. */
export default function VoiceMeter({ micStream, remoteStream, muted }: { micStream: MediaStream | null; remoteStream: MediaStream | null; muted: boolean }) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    const root = rootRef.current;
    const AudioCtx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!root || !AudioCtx) return;
    const bars = [...root.querySelectorAll('span')] as HTMLSpanElement[];
    const ctx = new AudioCtx();
    void ctx.resume().catch(() => {});

    const attach = (stream: MediaStream | null) => {
      if (!stream) return null;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.55;
      source.connect(analyser);
      return { source, analyser, data: new Uint8Array(analyser.frequencyBinCount) };
    };

    const mic = attach(micStream);
    const bot = attach(remoteStream);
    const heights = bars.map(() => 3);
    let raf = 0;

    const tick = () => {
      const micBands = mic && !mutedRef.current ? readBands(mic.analyser, mic.data, 28) : null;
      const botBands = bot ? readBands(bot.analyser, bot.data, 62) : null;
      const micAvg = micBands ? bandEnergy(micBands) : 0;
      const botAvg = botBands ? bandEnergy(botBands) : 0;
      const botTalking = botAvg > 0.08 && botAvg >= micAvg * 0.85;
      const youTalking = micAvg > 0.05;
      const active = botTalking ? botBands : youTalking ? micBands : null;
      bars.forEach((bar, i) => {
        const target = active ? 3 + active[i] * 16 : 3;
        const follow = target > heights[i] ? 0.72 : 0.38;
        heights[i] = heights[i] * (1 - follow) + target * follow;
        bar.style.height = `${heights[i].toFixed(1)}px`;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      try { mic?.source.disconnect(); } catch { /* already closed */ }
      try { bot?.source.disconnect(); } catch { /* already closed */ }
      void ctx.close().catch(() => {});
    };
  }, [micStream, remoteStream]);

  return (
    <span ref={rootRef} className="agent-voice-meter" aria-hidden>
      {Array.from({ length: 7 }, (_, i) => <span key={i} />)}
    </span>
  );
}
